import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

// Helper function to extract article data from URL or route state
const getArticleData = (pathname, state) => {
  // This is a simplified example - you'll need to adjust based on your data fetching
  if (pathname.startsWith('/article/')) {
    const articleId = pathname.split('/').pop();
    // You would typically fetch article data here or get it from route state
    return state?.article || {
      title: 'Article Title',
      abstract: 'Default article description',
      keywords: ['bioanalysis', 'research'],
      authors: 'Author Name',
      publishedDate: new Date().toISOString()
    };
  }
  return null;
};

const DynamicTitle = () => {
  const location = useLocation();
  const article = getArticleData(location.pathname, location.state);

  const getTitle = () => {
    if (article) {
      return `${article.title} | Journal of Applied Bioanalysis`;
    }
    if (location.pathname.startsWith("/for-reviewers")) {
      return "For Reviewers - Journal of Applied Bioanalysis";
    } else if (
      location.pathname.startsWith("/information/article-processing-charges")
    ) {
      return "Article Processing Charges - Journal of Applied Bioanalysis";
    } else if (location.pathname.startsWith("/information/for-authors-2")) {
      return "For Authors - Journal of Applied Bioanalysis";
    } else if (
      location.pathname.startsWith(
        "/information/editorial-criteria-and-processes"
      )
    ) {
      return "Editorial criteria and processes - Journal of Applied Bioanalysis";
    } else if (location.pathname.startsWith("/auth")) {
      return "Authentication - Journal of Applied Bioanalysis";
    }

    switch (location.pathname) {
      case "/about":
        return "About - Journal of Applied Bioanalysis";
      case "/privacy":
        return "Privacy - Journal of Applied Bioanalysis";
      case "/terms-condition":
        return "Terms and Condition - Journal of Applied Bioanalysis";
      case "/copyright":
        return "Copyright - Journal of Applied Bioanalysis";
      case "/journal-information":
        return "Journal Information - Journal of Applied Bioanalysis";
      case "/submit-article":
        return "Submit Article - Journal of Applied Bioanalysis";
      case "/issue-archive":
        return "Issue Archive - Journal of Applied Bioanalysis";
      case "/current-issue":
        return "Current Issue - Journal of Applied Bioanalysis";
      case "/editorial-board-journal-of-applied-bioanalysis":
        return "Editorial Board - Journal of Applied Bioanalysis";
      case "/author/article":
        return "Author Articles - Journal of Applied Bioanalysis";
      case "/author/article-submission":
        return "Author Article Submission - Journal of Applied Bioanalysis";
      default:
        return "Journal of Applied Bioanalysis";
    }
  };

  const metaDescription = `The Journal of Applied Bioanalysis is a publication that stands at the forefront of advancements in the field of bioanalysis. This peer-reviewed journal serves as a vital platform for researchers, scientists, and practitioners to share and disseminate their cutting-edge research, innovative methodologies, and practical applications across various domains of bioanalysis.`;

  const getMetaTags = () => {
    if (article) {
      return (
        <>
          <meta name="description" content={article.abstract || 'Read the latest bioanalytical research article.'} />
          <meta name="keywords" content={article.keywords?.join(', ') || 'bioanalysis, research'} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="article" />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={article.abstract} />
          {article.image && <meta property="og:image" content={article.image} />}
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={article.title} />
          <meta name="twitter:description" content={article.abstract} />
          {article.image && <meta name="twitter:image" content={article.image} />}
          
          {/* Article-specific meta */}
          {article.authors && <meta name="author" content={article.authors} />}
          {article.publishedDate && <meta property="article:published_time" content={article.publishedDate} />}
        </>
      );
    }
    
    // Default meta tags for non-article pages
    return (
      <>
        <meta name="description" content="Journal of Applied Bioanalysis - A peer-reviewed journal publishing high-quality research in bioanalysis." />
        <meta name="keywords" content="bioanalysis, journal, research, applied bioanalysis, scientific journal" />
      </>
    );
  };

  return (
    <Helmet>
      <title>{getTitle()}</title>
      {getMetaTags()}
      {/* Common meta tags for all pages */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html;charset=UTF-8" />
      <link rel="canonical" href={window.location.href} />
      <meta name="metadescription" content={metaDescription} />
      <meta property="og:title" content={getTitle()} />
      <meta
        property="og:image"
        content="https://www.scopus.com/sourceid/21101122745/"
      />
    </Helmet>
  );
};

export default DynamicTitle;
